import React, { useState, useEffect, useRef } from 'react';
import './maf-tools.css';

const MAFTOOLS_URL = 'https://server-maftools.serve.scilifelab.se/app/server-maftools';

function MAFTools() {
  const [isLoading, setIsLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('Connecting to Docker container...');
  const iframeRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const probeIntervalRef = useRef(null);
  const iframeLoadedRef = useRef(false);
  const serverReadyRef = useRef(false);

  // Probe the server directly to detect when it's ready
  const probeServer = async () => {
    try {
      console.log('[MAFTools] Probing server...');
      const startTime = Date.now();

      // Fetch with no-cors mode to avoid CORS issues - we just care about response time
      // Actually, let's try a regular fetch first to see the response
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(MAFTOOLS_URL, {
        method: 'HEAD', // Just check if server responds, don't download content
        mode: 'no-cors', // This will give us an opaque response, but we can check if it succeeded
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;

      console.log(`[MAFTools] Server probe response in ${responseTime}ms, type: ${response.type}`);

      // With no-cors, we get an opaque response (type: 'opaque')
      // If the server is up and responding quickly, we know it's ready
      if (responseTime < 2000) {
        console.log('[MAFTools] Server responding quickly - container is ready!');
        return true;
      } else {
        console.log('[MAFTools] Server response slow - container may still be initializing');
        return false;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('[MAFTools] Server probe timed out - container not ready');
      } else {
        console.log('[MAFTools] Server probe error:', error.message);
      }
      return false;
    }
  };

  // Start probing and progress animation when component mounts
  useEffect(() => {
    console.log('[MAFTools] Component mounted, starting load sequence');
    let probeCount = 0;
    const maxProbes = 60; // Max 60 probes (30 seconds with 500ms interval)

    // Progress animation
    progressIntervalRef.current = setInterval(() => {
      setLoadProgress(prev => {
        if (prev < 85) {
          const increment = Math.random() * 2 + 0.3;
          return Math.min(prev + increment, 85);
        }
        return prev;
      });
    }, 500);

    // Server probing
    const runProbe = async () => {
      probeCount++;
      console.log(`[MAFTools] Probe attempt ${probeCount}/${maxProbes}`);

      const isReady = await probeServer();

      if (isReady) {
        console.log('[MAFTools] Server is ready!');
        serverReadyRef.current = true;
        setStatusMessage('Server ready, loading interface...');
        setLoadProgress(90);

        // Clear probe interval
        if (probeIntervalRef.current) {
          clearInterval(probeIntervalRef.current);
          probeIntervalRef.current = null;
        }

        // If iframe also loaded, we're done
        if (iframeLoadedRef.current) {
          finishLoading();
        }
      } else if (probeCount >= maxProbes) {
        console.log('[MAFTools] Max probes reached, proceeding anyway');
        serverReadyRef.current = true;
        if (probeIntervalRef.current) {
          clearInterval(probeIntervalRef.current);
          probeIntervalRef.current = null;
        }
        if (iframeLoadedRef.current) {
          finishLoading();
        }
      } else {
        // Update status message based on probe count
        if (probeCount > 10) {
          setStatusMessage('Container is starting up...');
        }
        if (probeCount > 20) {
          setStatusMessage('Still initializing, please wait...');
        }
      }
    };

    // Start probing immediately, then every 500ms
    runProbe();
    probeIntervalRef.current = setInterval(runProbe, 500);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (probeIntervalRef.current) {
        clearInterval(probeIntervalRef.current);
      }
    };
  }, []);

  const finishLoading = () => {
    console.log('[MAFTools] Both server ready and iframe loaded - finishing up');

    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    setLoadProgress(95);
    setStatusMessage('Almost ready...');

    // Small delay for Shiny JS to initialize after HTML loads
    setTimeout(() => {
      setLoadProgress(100);
      console.log('[MAFTools] Progress set to 100%');

      setTimeout(() => {
        setIsLoading(false);
        console.log('[MAFTools] Loading complete, overlay hidden');
      }, 300);
    }, 1000);
  };

  const handleIframeLoad = () => {
    console.log('[MAFTools] Iframe onLoad event fired');
    iframeLoadedRef.current = true;

    // If server is also ready, finish loading
    if (serverReadyRef.current) {
      finishLoading();
    } else {
      console.log('[MAFTools] Iframe loaded but waiting for server probe confirmation');
      setStatusMessage('Waiting for app initialization...');
      setLoadProgress(prev => Math.max(prev, 88));
    }
  };

  const handleIframeError = (error) => {
    console.error('[MAFTools] Iframe error:', error);
    setIsLoading(false);
  };

  return (
    <div className="maf-tools">
      {isLoading && (
        <div className="maf-loading-overlay">
          <div className="maf-loading-content">
            <div className="maf-loading-spinner"></div>
            <h3 className="maf-loading-title">Initializing MAF Tools</h3>
            <p className="maf-loading-subtitle">{statusMessage}</p>
            <div className="maf-loading-progress-container">
              <div
                className="maf-loading-progress-bar"
                style={{ width: `${loadProgress}%` }}
              ></div>
            </div>
            <span className="maf-loading-percentage">{Math.round(loadProgress)}%</span>
          </div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        src="https://server-maftools.serve.scilifelab.se/app/server-maftools"
        width="100%"
        height="800"
        style={{ border: 'none' }}
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        title="MAF Tools"
      />
    </div>
  );
}

export default MAFTools;

